import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";

type Task = {
  taskTypeID: number;
  taskName: string;
  active: boolean;
  uploadDay: number;
  user: string | number;
  tribe: string[];
  schedule?: {
    frequency: "daily" | "weekly" | "monthly";
    dayOfWeek: number[];
    monthsInterval?: number;
    startDate?: string;
  };
};

type StartDateDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (tasksWithStartDate: Task[]) => void;
  tasks: Task[];
  loading?: boolean;
};

const StartDateDialog = ({
  open,
  onClose,
  onConfirm,
  tasks,
  loading = false,
}: StartDateDialogProps) => {
  const [localTasks, setLocalTasks] = useState<Task[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const tasksWithInterval = tasks.filter(
    (task) =>
      task.active &&
      task.schedule?.frequency === "monthly" &&
      (task.schedule?.monthsInterval || 1) >= 2,
  );

  useEffect(() => {
    if (open) {
      setLocalTasks([...tasksWithInterval]);
    }
  }, [open, tasks, tasksWithInterval]);

  const handleStartDateChange = (taskIndex: number, value: string) => {
    const updatedTasks = [...localTasks];
    if (!updatedTasks[taskIndex].schedule) {
      updatedTasks[taskIndex].schedule = {
        frequency: "monthly",
        dayOfWeek: [],
        monthsInterval: 1,
      };
    }
    updatedTasks[taskIndex].schedule!.startDate = value;
    setLocalTasks(updatedTasks);
  };

  const toISOStringIfNeeded = (dateString?: string) => {
    if (!dateString) return undefined;
    const [day, month, year] = dateString.split("/").map(Number);
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toISOString();
  };

  const buildConfirmedTasksPayload = () => {
    const allTasks = tasks.map((task) => {
      const updatedTask = localTasks.find(
        (lt) => lt.taskTypeID === task.taskTypeID,
      );
      if (updatedTask) {
        const taskWithConvertedDate = { ...updatedTask };
        if (taskWithConvertedDate.schedule) {
          taskWithConvertedDate.schedule.startDate = toISOStringIfNeeded(
            taskWithConvertedDate.schedule.startDate,
          );
        }
        return taskWithConvertedDate;
      }
      return task;
    });
    return allTasks;
  };

  const handleConfirm = () => {
    setConfirmOpen(true);
  };

  const formatDateInput = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4)
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(
      4,
      8,
    )}`;
  };

  const isValidDate = (dateString: string) => {
    if (!dateString || dateString.length !== 10) return false;
    const [day, month, year] = dateString.split("/").map(Number);
    if (!day || !month || !year) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    return true;
  };

  const allDatesValid = localTasks.every(
    (task) => !task.schedule?.startDate || isValidDate(task.schedule.startDate),
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Configurar Data de Início</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            As tarefas abaixo possuem intervalo de recorrência de 2 ou mais
            meses. Configure a data de início para cada uma delas:
          </Typography>

          {tasksWithInterval.length === 0 ? (
            <Typography>
              Nenhuma tarefa ativa encontrada com intervalo de 2 ou mais meses.
            </Typography>
          ) : (
            <List>
              {localTasks.map((task, index) => (
                <ListItem
                  key={task.taskTypeID}
                  sx={{ flexDirection: "column", alignItems: "stretch" }}
                >
                  <Box sx={{ width: "100%", mb: 1 }}>
                    <ListItemText
                      primary={task.taskName}
                      secondary={`Intervalo: ${
                        task.schedule?.monthsInterval || 1
                      } mês(es)`}
                    />
                  </Box>
                  <TextField
                    label="Data de início"
                    placeholder="dd/mm/yyyy"
                    value={task.schedule?.startDate || ""}
                    onChange={(e) => {
                      const formatted = formatDateInput(e.target.value);
                      handleStartDateChange(index, formatted);
                    }}
                    fullWidth
                    size="small"
                    error={
                      task.schedule?.startDate
                        ? !isValidDate(task.schedule.startDate)
                        : false
                    }
                    helperText={
                      task.schedule?.startDate &&
                      !isValidDate(task.schedule.startDate)
                        ? "Data inválida"
                        : ""
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={
              loading || !allDatesValid || tasksWithInterval.length === 0
            }
          >
            {loading ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Segundo diálogo de confirmação (menor) */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar competência inicial</DialogTitle>
        <DialogContent>
          {localTasks.map((task) => {
            const start = task.schedule?.startDate || "";
            let preview = "-";
            if (isValidDate(start)) {
              const [d, m, y] = start.split("/").map(Number);
              const now = new Date();
              const currentYm = new Date(now.getFullYear(), now.getMonth(), 1);
              let next = new Date(y, m - 1 + 1, 1);
              while (
                next.getFullYear() < currentYm.getFullYear() ||
                (next.getFullYear() === currentYm.getFullYear() &&
                  next.getMonth() <= currentYm.getMonth())
              ) {
                next = new Date(next.getFullYear(), next.getMonth() + 12, 1);
              }
              const mm = String(next.getMonth() + 1).padStart(2, "0");
              const yyyy = String(next.getFullYear());
              preview = `${mm}/${yyyy}`;
            }
            return (
              <Box key={task.taskTypeID} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {task.taskName}: A primeira tarefa será criada na competência{" "}
                  {preview}, e assim por diante com o intervalo selecionado.
                  Confirma?
                </Typography>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const payload = buildConfirmedTasksPayload();
              setConfirmOpen(false);
              onConfirm(payload);
            }}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StartDateDialog;
